import React, { useMemo } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add16Regular,
  CheckmarkCircle20Regular,
  Delete20Regular,
  DismissCircle20Regular,
  DoorTag24Regular,
  Edit20Regular,
  MoreHorizontal20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Consultorio, Sector, Sede } from '../../../../models/Entities/Turnero';
import { useOfficesStyles } from '../Offices.Styles';
import {
  ConsultorioFilterKey,
  LocationFilter,
  consultorioMatchesStatus,
  countConsultorios,
  filterConsultorios,
  findById,
  getSectorSedeId,
  normalizeConsultorioStatus,
} from '../Offices.Functions';
import {
  ConsultorioStatusPill,
  EmptyState,
  FilterPills,
  ListSkeleton,
  LocationFilters,
} from '../OfficesCommon/OfficesCommon.Component';

export type ConsultoriosFilter = LocationFilter & { status: ConsultorioFilterKey };

export interface ConsultoriosTabProps {
  consultorios: Consultorio[];
  sectores: Sector[];
  sedes: Sede[];
  isLoading: boolean;
  filter: ConsultoriosFilter;
  selectedId?: number;
  onFilterChange: (filter: ConsultoriosFilter) => void;
  onCreate: () => void;
  onEdit: (consultorio: Consultorio) => void;
  onToggleStatus: (consultorio: Consultorio) => void;
  onDelete: (consultorio: Consultorio) => void;
}

const ConsultoriosTab = ({
  consultorios,
  sectores,
  sedes,
  isLoading,
  filter,
  selectedId,
  onFilterChange,
  onCreate,
  onEdit,
  onToggleStatus,
  onDelete,
}: ConsultoriosTabProps) => {
  const { t } = useTranslation();
  const styles = useOfficesStyles();

  // Los contadores de las pastillas respetan búsqueda y ubicación, no el estado elegido.
  const located = useMemo(() => filterConsultorios(consultorios, { ...filter, status: 'all' }, sectores), [consultorios, filter, sectores]);
  const counts = useMemo(() => countConsultorios(located), [located]);
  const visible = useMemo(() => located.filter((c) => consultorioMatchesStatus(c, filter.status)), [located, filter.status]);

  const locationLabel = (c: Consultorio) => {
    const sector = findById(sectores, c.sectorId);
    if (!sector) return t('Offices.consultorio.noSector');
    const sede = findById(sedes, getSectorSedeId(sector));
    return [sector.name, sede?.name].filter(Boolean).join(' · ');
  };

  const renderContent = () => {
    if (isLoading && consultorios.length === 0) return <ListSkeleton testId="consultorios-skeleton" />;
    if (consultorios.length === 0) {
      return (
        <EmptyState
          testId="consultorios-empty"
          icon={<DoorTag24Regular />}
          title={t('Offices.consultorio.emptyTitle')}
          description={t('Offices.consultorio.emptyText')}
          actionLabel={t('Offices.consultorio.create')}
          actionIcon={<Add16Regular />}
          onAction={onCreate}
        />
      );
    }
    if (visible.length === 0) {
      return <EmptyState icon={<Search20Regular />} title={t('Offices.noResultsTitle')} description={t('Offices.noResultsText')} testId="consultorios-no-results" />;
    }
    return (
      <div className={styles.surface}>
        <Table aria-label={t('Offices.tabs.consultorios')} className={styles.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.headerCell}>{t('Offices.columns.consultorio')}</TableHeaderCell>
              <TableHeaderCell className={styles.headerCell}>{t('Offices.columns.location')}</TableHeaderCell>
              <TableHeaderCell className={styles.headerCell}>{t('Offices.columns.doctor')}</TableHeaderCell>
              <TableHeaderCell className={mergeClasses(styles.headerCell, styles.numericHeader)} style={{ width: '120px' }}>
                {t('Offices.columns.maxTurnos')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '170px' }}>
                {t('Offices.columns.status')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '64px' }} aria-label={t('Offices.columns.actions')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((c) => {
              const disabled = normalizeConsultorioStatus(c.status) === 'noDisponible';
              const selected = selectedId !== undefined && c.id === selectedId;
              return (
                <TableRow
                  key={c.id}
                  className={mergeClasses(styles.row, selected && styles.rowSelected, disabled && styles.rowInactive)}
                  aria-selected={selected}
                  data-testid={`consultorio-row-${c.id}`}
                >
                  <TableCell>
                    <div className={styles.primaryCell}>
                      <Button appearance="transparent" className={styles.linkButton} onClick={() => onEdit(c)}>
                        {c.name || c.number || `#${c.id}`}
                      </Button>
                      {c.description && <span className={styles.secondaryText}>{c.description}</span>}
                    </div>
                  </TableCell>
                  <TableCell>{locationLabel(c)}</TableCell>
                  <TableCell>
                    <span className={styles.secondaryText}>
                      {c.assignedDoctorFullName || (c.medicoId ? t('Offices.consultorio.doctorAssigned') : t('Offices.consultorio.noDoctor'))}
                    </span>
                  </TableCell>
                  <TableCell className={styles.numericCell}>{c.maxTurnosSimultaneos ?? 1}</TableCell>
                  <TableCell>
                    <ConsultorioStatusPill consultorio={c} />
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button appearance="subtle" icon={<MoreHorizontal20Regular />} aria-label={t('Offices.actions.moreFor', { name: c.name })} />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem icon={<Edit20Regular />} onClick={() => onEdit(c)}>
                              {t('Offices.actions.edit')}
                            </MenuItem>
                            <MenuItem icon={disabled ? <CheckmarkCircle20Regular /> : <DismissCircle20Regular />} onClick={() => onToggleStatus(c)}>
                              {disabled ? t('Offices.consultorio.enable') : t('Offices.consultorio.disable')}
                            </MenuItem>
                            <MenuItem icon={<Delete20Regular />} className={styles.dangerItem} onClick={() => onDelete(c)}>
                              {t('Offices.actions.delete')}
                            </MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <section className={styles.section} aria-label={t('Offices.tabs.consultorios')}>
      <div className={styles.toolbar}>
        <LocationFilters
          filter={filter}
          sedes={sedes}
          sectores={sectores}
          searchPlaceholder={t('Offices.consultorio.searchPlaceholder')}
          onChange={(next) => onFilterChange({ ...filter, ...next })}
        />
        <div className={styles.toolbarActions}>
          <Button appearance="primary" icon={<Add16Regular />} onClick={onCreate}>
            {t('Offices.consultorio.create')}
          </Button>
        </div>
      </div>
      <FilterPills<ConsultorioFilterKey>
        ariaLabel={t('Offices.filters.status')}
        selected={filter.status}
        onSelect={(status) => onFilterChange({ ...filter, status })}
        options={[
          { key: 'all', label: t('Offices.filters.all'), count: counts.all },
          { key: 'disponible', label: t('Offices.status.disponible'), count: counts.disponible, dot: 'success' },
          { key: 'ocupado', label: t('Offices.status.ocupado'), count: counts.ocupado, dot: 'warning' },
          { key: 'noDisponible', label: t('Offices.status.noDisponible'), count: counts.noDisponible, dot: 'neutral' },
          { key: 'sinSector', label: t('Offices.filters.withoutSector'), count: counts.sinSector, dot: 'brand' },
        ]}
      />
      {renderContent()}
    </section>
  );
};

export default ConsultoriosTab;
